import { productPrice } from "../../utils/productPricing";
import React, { useEffect, useState } from 'react'
import { AiFillHeart, AiOutlineShoppingCart } from 'react-icons/ai'
import { Link, useNavigate } from 'react-router-dom'
import { FaEye } from 'react-icons/fa'
import Ratings from '../Ratings'
import toast from 'react-hot-toast'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from "../../components/ui/button";
import { Heart, ShoppingBag, Package, Minus, Store } from 'lucide-react';
import { add_to_card, messageClear, add_to_wishlist, get_cart_products, get_wishlist_products, remove_wishlist, delete_card_product } from '../../store/reducers/cardReducer'
import { formatCurrency, fetchExchangeRates } from "../../utils/currencyUtils"
import axios from '../../api/api'

const FadeInItem = ({ children, delay = 0 }) => {
  const ref = React.useRef(null);
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ease-out transform ${isVisible
        ? "opacity-100 translate-y-0 brightness-100 grayscale-0"
        : "opacity-0 translate-y-12 brightness-50 grayscale"
        }`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const ShopProducts = ({ styles, products, storeMode = false, customSettings }) => {

  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [currencyCode, setCurrencyCode] = useState("NGN");
  const [exchangeLoaded, setExchangeLoaded] = useState(false);
  const [imgErrors, setImgErrors] = useState({});
  const { userInfo } = useSelector(state => state.auth)
  const { successMessage, errorMessage, card_products, wishlist } = useSelector(state => state.card)

  const isProductInCart = (productId) => {
    if (!card_products) return false;
    return card_products.some(shop =>
      shop.products.some(item => item.productInfo._id === productId)
    );
  };

  const isProductInWishlist = (productId) => {
    if (!wishlist) return false;
    return wishlist.some(item => item.productId === productId);
  };

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const { data } = await axios.get("/account/get-details", {
          withCredentials: true,
        });
        if (data.currency) {
          setCurrencyCode(data.currency);
        }
      } catch (error) {
        // Handle error silently
      }
    };

    if (userInfo && userInfo.id) {
      fetchUserDetails();
    }
  }, []);
  useEffect(() => {
    fetchExchangeRates().then(() => setExchangeLoaded(true));
  }, []);
  const add_card = (id) => {
    const product = products.find(p => p._id === id);
    if (product?.priceOptions?.length) { navigate(storeMode ? `/${product.shopName}/product/details/${product._id}` : `/product/details/${product._id}`); return; }
    const cartItemId = card_products?.flatMap(shop => shop.products).find(item => item.productInfo._id === id)?._id;

    if (cartItemId) {
      dispatch(delete_card_product({
        card_id: cartItemId,
        storeName: storeMode ? product.shopName : null
      }));
    } else {
      // If we are in storeMode (which implies we are on a store page), we should pass the storeName
      // But ShopProducts receives `products`. It doesn't explicitly receive `storeName` prop, 
      // but the products have `shopName`.
      // However, to be safe and consistent with "peculiar to a store", we should probably use the storeName from the URL or context.
      // But `ShopProducts` is used in `StoreDetails`.
      // Let's check if we can get storeName from product or context.
      // The `add_to_card` thunk now supports `storeName`.

      // If storeMode is true, we assume we want store-specific behavior.
      // We can get the store name from the product itself (product.shopName)

      dispatch(add_to_card({
        userId: userInfo ? userInfo.id : null,
        quantity: 1,
        productId: id,
        productInfo: product,
        storeName: storeMode ? product.shopName : null
      }))
    }
  }
  useEffect(() => {
    if (successMessage) {
      // toast.success(successMessage)
      dispatch(messageClear())
      // We need to refresh the cart. If in storeMode, we should refresh with storeName.
      // But here we don't have easy access to the "current store context" unless we pass it down or infer it.
      // `products` array usually belongs to one store in `ShopProducts` when used in `StoreDetails`.
      const currentStoreName = storeMode && products.length > 0 ? products[0].shopName : null;

      if (userInfo) {
        dispatch(get_cart_products(currentStoreName ? { userId: userInfo.id, storeName: currentStoreName } : userInfo.id));
        dispatch(get_wishlist_products(currentStoreName ? { userId: userInfo.id, storeName: currentStoreName } : userInfo.id));
      } else {
        dispatch(get_cart_products(currentStoreName ? { userId: null, storeName: currentStoreName } : null));
        dispatch(get_wishlist_products(currentStoreName ? { userId: null, storeName: currentStoreName } : null));
      }
    }
  }, [errorMessage, successMessage, userInfo, dispatch])

  const add_wishlist = (pro) => {
    const existingWishlistItem = wishlist.find(item => item.productId === pro._id);
    if (existingWishlistItem) {
      dispatch(remove_wishlist({
        wishlistId: existingWishlistItem._id,
        storeName: storeMode ? pro.shopName : null
      }));
    } else {
      dispatch(add_to_wishlist({
        userId: userInfo ? userInfo.id : null,
        productId: pro._id,
        name: pro.name,
        price: pro.price,
        image: pro.images[0],
        discount: pro.discount,
        rating: pro.rating,
        slug: pro.slug,
        shopName: pro.shopName,
        currency: pro.currency,
        storeName: storeMode ? pro.shopName : null
      }))
    }
  }

  // Create CSS variables from customSettings with theme-aware defaults
  const productStyle = {
    // Colors stay theme-aware so sections always have background/text colors
    '--product-card-bg': customSettings?.cardBg || 'var(--store-product-card-bg)',
    '--product-price-color': customSettings?.priceColor || 'var(--store-text)',
    '--product-name-color': customSettings?.nameColor || 'var(--store-text)',
    '--product-button-bg': customSettings?.buttonBg || 'var(--store-button-bg)',
    '--product-button-text': customSettings?.buttonText || 'var(--store-button-text)',
    '--product-wishlist-bg': customSettings?.wishlistBg || 'var(--store-glass)',
    '--product-wishlist-color': customSettings?.wishlistColor || 'var(--store-text)',

    // Numerical overrides - ONLY set if explicitly customized to allow CSS class fallbacks (like rounded-full)
    '--product-card-radius': customSettings?.cardRadius !== undefined ? `${customSettings.cardRadius}px` : undefined,
    '--product-price-size': customSettings?.priceSize !== undefined ? `${customSettings.priceSize}px` : undefined,
    '--product-name-size': customSettings?.nameSize !== undefined ? `${customSettings.nameSize}px` : undefined,
    '--product-button-radius': customSettings?.buttonRadius !== undefined ? `${customSettings.buttonRadius}px` : undefined,
    '--product-image-height': customSettings?.imageHeight !== undefined ? `${customSettings.imageHeight}px` : undefined,

    // Layout gap
    gap: customSettings?.cardGap !== undefined ? `${customSettings.cardGap}px` : undefined
  };

  return (
    <>
      {products.length === 0 ? (
        <div className="text-center text-[var(--store-text)] font-semibold py-6">
          No products found.
          <Link to="/products">
            <Button className="m-2">
              Browse Other Products
            </Button>
          </Link>
        </div>

      ) : (
        <div
          className={` ${styles === 'grid'
            ? (products.length === 1 ? 'grid-cols-3 md-lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-1 grid' : 'grid-cols-3 md-lg:grid-cols-2 md:grid-cols-2 sm:grid-cols-2 grid')
            : 'w-[400px] md-lg:w-auto grid-cols-3 md-lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1'} gap-3`
          }
          style={productStyle}
        >
          {products.map((p, i) => (
            <FadeInItem key={i} delay={(i % 6) * 150}>
              <div
                className="group transition-all duration-500 hover:shadow-md cursor-pointer"
                style={{
                  backgroundColor: 'var(--product-card-bg)',
                  borderRadius: 'var(--product-card-radius, 8px)'
                }}
              >
                <div className="relative overflow-hidden">
                  {p.discount ? (
                    <div className="flex justify-center items-center absolute text-white py-[2px] px-6 rounded-full bg-red-600 font-semibold text-s left-2 top-2">
                      {p.discount}%
                    </div>
                  ) : ("")}
                  {isProductInCart(p._id) && (
                    <div className="flex justify-center items-center absolute text-white w-[38px] h-[38px] rounded-full bg-[#FFE11B] font-semibold text-xs right-2 top-2 z-10 shadow-lg transition-all duration-500">
                      <ShoppingBag size={20} className="text-black" />
                    </div>
                  )}
                  <Link to={p.shopName ? `/${p.shopName}/product/details/${p._id}` : `/product/details/${p._id}`} state={storeMode ? { fromStore: p.shopName } : null} className="">
                    {!p.images?.[0] || imgErrors[p._id] ? (
                      <div
                        className="sm:w-full w-full flex items-center justify-center bg-gray-100 dark:bg-gray-800"
                        style={{ height: 'var(--product-image-height, 240px)' }}
                      >
                        <Store className="text-gray-400" size={48} />
                      </div>
                    ) : (
                      <img
                        className="sm:w-full w-full nex_img_cover"
                        style={{ height: 'var(--product-image-height, 240px)' }}
                        src={p.images[0]}
                        onError={() => setImgErrors(prev => ({ ...prev, [p._id]: true }))}
                        alt="product image"
                      />
                    )}
                  </Link>
                </div>
                <div className="py-3 text-[var(--store-text)] px-2">
                  <div className="p-2 flex justify-between items-center">
                    <span className="font-bold" style={{ color: 'var(--product-price-color)', fontSize: 'var(--product-price-size, 18px)' }}>{productPrice(p, currencyCode)}</span>
                  </div>
                  <div className="p-2">
                    <h2 className="truncate" style={{ color: 'var(--product-name-color)', fontSize: 'var(--product-name-size, 14px)' }}>{p.name} ({p.stock})</h2>
                  </div>
                  <div className="gap-3 p-2">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex">
                        <Ratings ratings={p.rating} />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => add_card(p._id)}
                        className="flex-1 justify-center flex items-center nex_button_main font-semibold sm:text-sm sm:py-1"
                        style={{
                          backgroundColor: 'var(--product-button-bg)',
                          color: 'var(--product-button-text)',
                          borderRadius: 'var(--product-button-radius, 100px)'
                        }}
                      >
                        {p.priceOptions?.length ? 'Choose options' : isProductInCart(p._id) ? 'Remove' : 'Add to cart'}
                      </button>
                      <div
                        onClick={() => add_wishlist(p)}
                        className="w-[38px] h-[38px] cursor-pointer rounded-full flex justify-center items-center backdrop-blur-md border border-[var(--store-border)] transition-all"
                        style={{ backgroundColor: 'var(--product-wishlist-bg)' }}
                      >
                        <Heart size={20} className={`${isProductInWishlist(p._id) ? 'fill-red-500 text-red-500' : ''}`} style={{ color: isProductInWishlist(p._id) ? undefined : 'var(--product-wishlist-color)' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </FadeInItem>
          ))}
        </div>
      )}
    </>
  );

}

export default ShopProducts